import { proStatusLine } from '@/lib/proStatusLine';
import { useEffect, useRef, useState } from 'react';
import { BrandSelect } from '@/components/BrandSelect';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation, useMutationState } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { openStoredFile, storedFileOf, useDisplayPhotoUrl, useSignedStoragePath } from '@/lib/storagePhoto';
import { useSession } from '@/lib/auth';
import { useActiveLocationId } from '@/lib/locationContext';
import { factsFromRows } from '@/lib/studioFacts';
import { useRealtimeTable } from '@/lib/useRealtimeTable';
import { QrCodeCard } from '@/components/QrCodeCard';
import { useFeature } from '@/lib/useFeature';
import { doorOrigin } from '@/lib/hosts';
import { saveProProfile, crmMirrorProblem } from '@/lib/proProfile';
import { isCell, CELL_OWNER_HUB_ORIGIN } from '@/lib/cell/config';
import { ownerHubDoorOrigin, ownerHubToken } from '@/lib/cell/ownerHub';
import { cellGet } from '@/lib/cell/api';
import { cellSign } from '@/lib/cell/files';
import { DISPLAY_TTL_SECONDS } from '@/lib/storagePhoto';

const LICENSE_TYPES = [
  'Cosmetologist', 'Esthetician', 'Nail Technician', 'Massage Therapist',
  'Barber', 'Hairstylist', 'Other',
];

/**
 * Pro's home screen. Pulls the full Hub view in one batched fetch:
 *   pros → tenants → suites → location_configs + compliance_records
 *
 * RLS allows a Pro to read their own row across each of those tables
 * (policies named `pros_read_own_*`). Operators with `app_can_read_location`
 * still see everything for their location through the existing policies.
 */
type Tenant = {
  id: string;
  location_id: string;
  pro_id: string | null;
  suite_id: string | null;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  specialty: string | null;
  instagram_handle: string | null;
  business_instagram: string | null;
  facebook_url: string | null;
  website_url: string | null;
  status: string;
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  emergency_contact_relationship: string | null;
  hub_access_code: string | null;
  access_code: string | null;
  salon_name: string | null;
  home_address: string | null;
  tenure: string | null;
  how_heard: string | null;
  short_bio: string | null;
  services_offered: string | null;
  zoho_synced_at: string | null;
};

type Suite = {
  id: string;
  suite_number: number;
  square_feet: number | null;
};

/** The studio facts this page shows, all of them from My Studio.
 *  hub_branding, social, resources_url and resources_label were fetched here
 *  and rendered NOWHERE — dropped rather than carried across (resources_url
 *  still held the literal placeholder REPLACE_WITH_REAL_FOLDER_ID). */
type LocationConfig = {
  location_id: string;
  display_name: string;
  address: string;
  phone: string | null;
  wifi: {
    pro?: { ssid?: string; password?: string };
    guest?: { ssid?: string; password?: string };
  } | null;
};

type ComplianceRecord = {
  /** COMPLIANCE-0: 'license' | 'insurance', generated in the database from record_type. */
  kind?: string | null;
  id: string;
  record_type: string;
  document_type: string | null;
  document_number: string | null;
  carrier: string | null;
  issue_date: string | null;
  expiry_date: string | null;
  verification_status: string;
  document_url: string | null;
};

type HubMyInfo = {
  /** 958b001e: the last CRM copy of this tenant's save, on the AWS cell only (reduced §6 block; tenean-infra cell 0200). */
  crmMirror?: Record<string, unknown> | null;
  pro: {
    id: string;
    email: string;
    first_name: string | null;
    last_name: string | null;
    /**
     * ops-photos object path for this pro's headshot, or null.
     *
     * Lives on `pros` and not on the tenant row because a headshot belongs to
     * the PERSON: 15 pros hold two tenant rows (retired sharon_ma + live
     * sharon_zoho), and a per-tenancy photo would mean two competing photos for
     * those people. NULL is the placeholder state, not an error — a pro with no
     * Zoho headshot taps the ring and sets their own.
     */
    photo_path: string | null;
    /**
     * Durable URL of the headshot as a REAL media item — written by the media
     * door (/pro/photo) after the file lands in R2 and is indexed in hub.media.
     * Preferred over photo_path: a photo only the Hub can render is invisible to
     * the owner's Media room and unusable in a post.
     */
    photo_url: string | null;
  };
  tenant: Tenant | null;
  suite: Suite | null;
  location: LocationConfig | null;
  license: ComplianceRecord | null;
  insurance: ComplianceRecord | null;
};

// Phase 4e (2026-04-26): legacy_fields was dropped; this helper now
// always returns null. Kept as a stub to avoid touching every fallback
// expression; the top-level columns supply the real data and the
// fallbacks now degrade to no-ops.
function readLegacy<T = string>(_t: Tenant | null, _key: string): T | null {
  return null;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '—';
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return raw;
}

function isExpired(iso: string | null | undefined): boolean {
  if (!iso) return true;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) || d.getTime() < Date.now();
}

function initials(first: string | null | undefined, last: string | null | undefined): string {
  const a = (first ?? '').trim().charAt(0).toUpperCase();
  const b = (last ?? '').trim().charAt(0).toUpperCase();
  return (a + b) || '?';
}

const PHOTO_BUCKET = 'ops-photos' as const;

/**
 * Where the media door lives. The Pro Hub and the owner app are different origins, so this
 * is a cross-origin POST and the door answers the preflight for exactly this host — a
 * refused preflight shows up only as "Failed to fetch", naming nothing.
 */
const MEDIA_DOOR = doorOrigin();

/** useSignedStoragePath's cell twin (move map M28): a private object's signed link from /v1/pro-hub/files/sign, null
 *  while it is minted or when the bucket's rule refuses — never a dead link. */
function useCellSignedPath(bucket: 'ops-photos', path: string | null | undefined): string | null {
  const [signed, setSigned] = useState<{ for: string; url: string | null } | null>(null);
  useEffect(() => {
    if (!path) return;
    let live = true;
    // CELL (M28): storage.from(bucket).createSignedUrl → POST /v1/pro-hub/files/sign.
    void cellSign(bucket, path, DISPLAY_TTL_SECONDS).then((r) => { if (live) setSigned({ for: path, url: r.data?.signedUrl ?? null }); });
    return () => { live = false; };
  }, [bucket, path]);
  return path && signed?.for === path ? signed.url : null;
}
const MAX_PHOTO_BYTES = 10 * 1024 * 1024;

/**
 * The pro's headshot in the black hero card — and the one place they change it.
 *
 * SEEDED, THEN OWNED. `pros.photo_path` starts as whatever Zoho's onboarding
 * headshot was (pro-profile-harvest puts it there) and becomes the pro's own the
 * moment they pick a new one. Nothing that seeds the column may overwrite a
 * non-null value, so this upload is always the last word.
 *
 * NO EDIT BADGE — Peter's pick from the mockups: the photo itself is the button,
 * which keeps the card clean. The empty state carries the whole affordance
 * instead (a dashed pink ring + "+"), because that is the moment a pro actually
 * needs to be told they can do something.
 *
 * WRITES DIRECTLY, NOT THROUGH pro-profile-update. That Edge Function is the
 * single write path for My Info *because those fields mirror into Zoho* — and
 * photos are deliberately Zoho-exempt (Peter, 2026-08-18: "no need to push back
 * to zoho"). With no CRM leg there is nothing for the function to do that RLS
 * does not already do: `authed_update_pros` scopes on `auth_user_id = auth.uid()`,
 * verified against production as a real pro (she can write her own row; the same
 * statement against another pro's row returns zero rows).
 */
export function ProPhoto({
  tenantId, locationId, photoPath, photoUrl, firstName, lastName, onChanged,
}: {
  // proId is deliberately absent: the door writes the row for whoever the token
  // belongs to, so the screen no longer needs — or gets to choose — the pro id.
  tenantId: string | null;
  locationId: string | null;
  photoPath: string | null;
  /** Durable media-door URL; wins over photoPath when present. */
  photoUrl: string | null;
  firstName: string | null;
  lastName: string | null;
  onChanged: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // A photo can only be written into a folder whose first segment is a location
  // the caller may access (ops_photos_authed_insert keys on foldername[1]). A pro
  // with no tenancy row has no such location, so they see their photo but get no
  // picker rather than a button that would always fail.
  const canEdit = !!tenantId && !!locationId;

  // The door's URL wins. photo_path is the legacy ops-photos path the Zoho harvester
  // still writes, so it stays the fallback rather than being migrated — a pro seeded
  // from Zoho keeps their photo until their next upload goes through the door.
  //
  // SIGNED, NOT PUBLIC (security fix #53). ops-photos goes private, so the fallback is a
  // short-lived link minted with the pro's own session (ops_photos_authed_select). Kept rather
  // than dropped: 0 pros rely on it today (every photo_path pro also has photo_url, checked
  // 2026-09-25), but pro-profile-harvest still writes photo_path alone for the next one.
  // Only asked for when there is no door URL, so the common case costs no request.
  const signedFallback = useSignedStoragePath(PHOTO_BUCKET, photoUrl || isCell() ? null : photoPath);
  // CELL (M28): the same fallback signed through /v1/pro-hub/files/sign (the bucket's own rule), never Supabase.
  const cellSigned = useCellSignedPath(PHOTO_BUCKET, isCell() && !photoUrl ? photoPath : null);
  const publicUrl = photoUrl ? photoUrl : (isCell() ? cellSigned : signedFallback);

  async function pick(input: HTMLInputElement) {
    const f = input.files?.[0];
    input.value = '';
    if (!f || !tenantId || !locationId) return;
    if (!f.type.startsWith('image/')) { setErr('That file is not an image.'); return; }
    if (f.size > MAX_PHOTO_BYTES) { setErr('That photo is larger than 10MB — try a smaller one.'); return; }

    setBusy(true); setErr(null);
    try {
      // THROUGH THE DOOR, not straight to storage (Peter: "build the media door").
      //
      // This screen used to upload to the ops-photos bucket and write the path on the
      // pro's row. That produced a photo only this screen could render: the owner's
      // Media room lists hub.media backed by R2, and a post can only use a /m/ path.
      // So the pro's own headshot was invisible to their studio and unusable in the
      // content it exists to appear in.
      //
      // The door stores the file in R2 under the STUDIO's tenant, indexes it in
      // hub.media so it shows up in Media, and writes the durable URL back onto the
      // pro — one motion, one place, the same shape as the one import door.
      //
      // The pro's own access token goes with it: the door verifies it against the auth
      // server and honours `location_id` only if it is in this pro's own pro_locations.
      // CELL (M28): the photo door is the Owner Hub's (/pro/photo). Until its AWS staging host is set it is not on
      // this build — say so; never call production.
      if (isCell() && !CELL_OWNER_HUB_ORIGIN) {
        throw new Error('photo upload is not on the AWS staging build yet.');
      }
      // CELL (M28): this build's door origin and token (lib/cell/ownerHub.ts) — the Cognito ID token on the cell.
      const token = await ownerHubToken();
      if (!token) throw new Error('Sign in again — your session has ended.');

      const body = new FormData();
      body.append('file', f);
      body.append('location_id', locationId);

      const res = await fetch(`${ownerHubDoorOrigin() ?? MEDIA_DOOR}/pro/photo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body,
      });
      // A cross-origin failure arrives as a thrown TypeError with no body, so read the
      // response defensively rather than assuming JSON.
      const out = await res.json().catch(() => null) as { ok?: boolean; error?: string } | null;
      if (!res.ok || !out?.ok) {
        throw new Error(out?.error || 'That photo did not save — try again.');
      }

      onChanged();
    } catch (e) {
      // Say what happened. A raw PostgREST/storage error string in the UI is how
      // a permission problem gets mistaken for a broken feature.
      //
      // supabase-js rejects with a PLAIN OBJECT ({ message, code, … }), not an
      // Error — so `e instanceof Error` is false and String(e) yields
      // "[object Object]". Read .message off the object first, or every real
      // failure prints that instead of the sentence written for it. Caught by
      // MyInfo.photo.test.tsx rather than by a pro seeing it.
      const raw =
        e instanceof Error
          ? e.message
          : (typeof e === 'object' && e !== null && typeof (e as { message?: unknown }).message === 'string')
            ? (e as { message: string }).message
            : String(e);
      setErr(/row-level security|permission|denied/i.test(raw)
        ? 'Could not save that photo — your session may need to refresh. Try again in a moment.'
        : `Could not save that photo: ${raw}`);
    } finally {
      setBusy(false);
    }
  }

  // Held to the avatar: the "Change photo" label used to widen this column to 112px and take
  // 16px from the name for a caption nobody measures by (measured 2026-09-08).
  return (
    <div className="shrink-0 w-24">
      <button
        type="button"
        disabled={!canEdit || busy}
        onClick={() => fileRef.current?.click()}
        aria-label={publicUrl ? 'Change your photo' : 'Add your photo'}
        className={[
          'size-24 rounded-full overflow-hidden flex items-center justify-center',
          'font-bold text-xl transition',
          canEdit ? 'cursor-pointer hover:opacity-80' : 'cursor-default',
          busy ? 'opacity-50' : '',
          publicUrl
            ? ''
            : canEdit
              // Empty state carries the affordance: dashed ring + "+".
              ? 'border-2 border-dashed border-image-pink text-image-pink-light'
              : 'bg-image-pink-gradient text-white',
        ].join(' ')}
      >
        {publicUrl
          ? <img src={publicUrl} alt="" className="size-full object-cover" />
          : canEdit
            ? <span aria-hidden className="text-3xl">+</span>
            : <span>{initials(firstName, lastName)}</span>}
      </button>
      {canEdit && (
        <button type="button" disabled={busy}
          onClick={() => fileRef.current?.click()}
          className="mt-1.5 block w-full text-center text-[11px] font-semibold text-image-pink-soft hover:text-white transition-colors">
          {publicUrl ? 'Change photo' : 'Add photo'}
        </button>
      )}
      {canEdit && (
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => { void pick(e.currentTarget); }}
        />
      )}
      {err && <div className="mt-1 text-[10px] text-image-pink-light max-w-[8rem]">{err}</div>}
    </div>
  );
}

type CellMyInfo = {
  pro: { id: string; email: string | null; first_name: string; last_name: string; photo_path: string | null; photo_url: string | null } | null;
  tenant: Tenant | null;
  suite: Suite | null;
  kb: Array<{ key: string; value: string | null }> | null;
  compliance: ComplianceRecord[] | null;
  /** 958b001e (tenean-infra home.ts myInfoLoad): the last CRM copy, { event_id, block }, or null */
  crm_mirror?: { event_id: string; block: Record<string, unknown> } | null;
};

/** The same HubMyInfo the queryFn builds, from ONE call on the AWS cell (move map M28). A failed call throws, as a
 *  failed read does below (HUB-074: never an empty record a Save would then clear). */
async function cellMyInfo(userId: string, activeLocationId: string | null, userEmail: string): Promise<HubMyInfo | null> {
  const path = activeLocationId
    ? `/v1/pro-hub/locations/${encodeURIComponent(activeLocationId)}/users/${userId}/my-info`
    : `/v1/pro-hub/users/${userId}/my-info`;
  // CELL (M28): GET /v1/pro-hub/(locations/{locationId}/)users/{authUserId}/my-info — pros, tenants, suites, kb, compliance.
  const { data, error } = await cellGet<CellMyInfo>(path);
  if (error || !data) throw new Error('your studio record could not be read');
  const { pro, tenant } = data;
  if (!pro) return null;
  let location: LocationConfig | null = null;
  let license: ComplianceRecord | null = null;
  let insurance: ComplianceRecord | null = null;
  if (tenant) {
    const f = factsFromRows(data.kb ?? []);
    location = {
      location_id: tenant.location_id,
      display_name: f.displayName ?? '',
      address: f.address ?? '',
      phone: f.supportPhone,
      wifi: {
        pro: f.raw.wifi_pro_ssid
          ? { ssid: f.raw.wifi_pro_ssid, password: f.raw.wifi_pro_pw ?? '' } : undefined,
        guest: f.raw.wifi_guest_ssid
          ? { ssid: f.raw.wifi_guest_ssid, password: f.raw.wifi_guest_pw ?? '' } : undefined,
      },
    };
    const records = data.compliance ?? [];
    license = records.find(r => r.kind === 'license') ?? null;
    insurance = records.find(r => r.kind === 'insurance') ?? null;
  }
  return {
    pro: { id: pro.id, email: pro.email ?? userEmail, first_name: pro.first_name, last_name: pro.last_name, photo_path: pro.photo_path ?? null, photo_url: pro.photo_url ?? null },
    tenant: tenant ?? null,
    suite: data.suite ?? null,
    location,
    license,
    insurance,
    crmMirror: data.crm_mirror?.block ?? null,
  };
}

export default function MyInfo() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const userEmail = session?.user?.email ?? '';
  const queryClient = useQueryClient();

  const { activeLocationId } = useActiveLocationId();
  const growOn = useFeature('grow').enabled;

  // 958b001e: a My Info save that answered on this page (its answer carries production's zoho block)
  const savedHere = useMutationState({ filters: { status: 'success', predicate: (m) => !!(m.state.data as { zoho?: unknown } | undefined)?.zoho } }).length > 0;
  const { data, isLoading, error, refetch } = useQuery<HubMyInfo | null>({
    queryKey: ['my-info', userId, activeLocationId],
    enabled: !!userId,
    queryFn: async () => {
      // CELL (M28): the whole load is one endpoint on the AWS cell (cellMyInfo below); Supabase below is untouched.
      if (isCell()) return cellMyInfo(userId!, activeLocationId ?? null, userEmail);
      // 1. Pro row
      const { data: pro, error: proErr } = await supabase
        .from('pros')
        .select('id, email, first_name, last_name, photo_path, photo_url')
        .eq('auth_user_id', userId!)
        .maybeSingle();
      if (proErr) throw proErr;
      if (!pro) return null;

      // 2. Tenant row scoped to active location. Multi-location Pros
      // (Jessica @ Demo + Brookline) get the row matching the picker;
      // single-location Pros get their only row regardless. Falls back
      // to most-recent if activeLocationId is null (no LocationProvider
      // context, e.g., operator-only accounts) so the page still loads.
      // CELL (M28): production only — the cell path is the isCell() branch above, which returns first.
      const tenantQuery = supabase
        .from('tenants')
        .select('*')
        .eq('pro_id', pro.id);
      const { data: tenant, error: tenantErr } = activeLocationId
        ? await tenantQuery.eq('location_id', activeLocationId).maybeSingle<Tenant>()
        : await tenantQuery.order('updated_at', { ascending: false }).limit(1).maybeSingle<Tenant>();
      // HUB-074: a failed read is never an empty record. These rows feed the EDIT cards — a phone
      // that could not be read must not open as a blank box a Save would then clear — and the
      // licence/COI status, where "missing" sends a pro to re-file something already on file.
      if (tenantErr) throw new Error('your studio record could not be read');

      let suite: Suite | null = null;
      let location: LocationConfig | null = null;
      let license: ComplianceRecord | null = null;
      let insurance: ComplianceRecord | null = null;

      if (tenant) {
        if (tenant.suite_id) {
          // CELL (M28): production only — the cell path is the isCell() branch above, which returns first.
          const { data: s, error: suiteErr } = await supabase
            .from('suites')
            .select('id, suite_number, square_feet')
            .eq('id', tenant.suite_id)
            .maybeSingle<Suite>();
          if (suiteErr) throw new Error('your suite could not be read');
          suite = s ?? null;
        }
        // MY STUDIO IS THE SOURCE (Peter, 2026-08-26) — see lib/studioFacts.ts.
        // This page showed a pro their studio's address, Wi-Fi and phone from
        // location_configs, which no owner edits: the phone was Peter's personal
        // mobile, and the Wi-Fi was a SECOND COPY of My Studio's own values that
        // would go stale the moment an owner changed the password.
        // CELL (M28): production only — the cell path is the isCell() branch above, which returns first.
        const { data: kb, error: kbErr } = await supabase
          .from('location_kb_values')
          .select('key, value')
          .eq('location_id', tenant.location_id)
          .eq('ns', 'location');
        if (kbErr) throw new Error('your studio’s details could not be read');
        const f = factsFromRows(kb ?? []);
        location = {
          location_id: tenant.location_id,
          display_name: f.displayName ?? '',
          address: f.address ?? '',
          phone: f.supportPhone,
          wifi: {
            pro: f.raw.wifi_pro_ssid
              ? { ssid: f.raw.wifi_pro_ssid, password: f.raw.wifi_pro_pw ?? '' } : undefined,
            guest: f.raw.wifi_guest_ssid
              ? { ssid: f.raw.wifi_guest_ssid, password: f.raw.wifi_guest_pw ?? '' } : undefined,
          },
        };

        // COMPLIANCE-0 (2026-09-03): read the CURRENT filing of each kind and nothing else. A new
        // filing SUPERSEDES the old one now (superseded_at set) rather than sitting beside it, so
        // the old rows are still in the table — and the previous read here ("first row of the
        // kind that has a number, newest expiry first") would happily pick a REPLACED row over the
        // current one whenever the current filing has no number yet. `kind` is generated in the
        // database from record_type, so the legacy '*_license' spellings need no matching here.
        // CELL (M28): production only — the cell path is the isCell() branch above, which returns first.
        const { data: comp, error: compErr } = await supabase
          .from('compliance_records')
          .select('id, record_type, kind, document_type, document_number, carrier, issue_date, expiry_date, verification_status, document_url')
          .eq('tenant_id', tenant.id)
          .is('superseded_at', null);
        if (compErr) throw new Error('your licence and insurance filings could not be read');

        const records = (comp ?? []) as ComplianceRecord[];
        license = records.find(r => r.kind === 'license') ?? null;
        insurance = records.find(r => r.kind === 'insurance') ?? null;
      }

      return {
        pro: { id: pro.id, email: pro.email ?? userEmail, first_name: pro.first_name, last_name: pro.last_name, photo_path: pro.photo_path ?? null, photo_url: pro.photo_url ?? null },
        tenant: tenant ?? null,
        suite,
        location,
        license,
        insurance,
      };
    },
  });

  // Realtime: the My Info page is two records joined client-side
  // (tenant + compliance_records). Subscribe to both so the operator
  // editing a tenant on iOS or adding a license/insurance record
  // surfaces here within seconds — no refresh required. Filtering by
  // pro_id on tenants keeps the channel tight; compliance_records
  // doesn't have a pro_id column so we filter by tenant_id once we
  // know it (the page-level query above resolves tenant first).
  const proId = data?.pro.id;
  const tenantId = data?.tenant?.id;
  useRealtimeTable({
    table: 'tenants',
    filter: proId ? `pro_id=eq.${proId}` : undefined,
    invalidateKeys: [['my-info']],
    enabled: !!proId,
  });
  useRealtimeTable({
    table: 'compliance_records',
    filter: tenantId ? `tenant_id=eq.${tenantId}` : undefined,
    invalidateKeys: [['my-info']],
    enabled: !!tenantId,
  });

  if (isLoading) {
    return <div className="text-brand-slate text-sm">Loading…</div>;
  }
  if (error) {
    return (
      <div className="card text-sm text-red-600" role="alert" data-testid="myinfo-load-failed">
        Couldn't load your info — {String((error as Error).message)}. Nothing has changed on your record.{' '}
        <button type="button" className="underline" onClick={() => { void refetch(); }}>Try again</button>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="card">
        <h2 className="font-display text-lg text-brand-deep mb-2">Welcome</h2>
        <p className="text-sm text-brand-slate">
          We don't have a profile linked to <strong>{userEmail}</strong> yet.
          Your studio owner will add you and you'll be all set on next sign-in.
        </p>
      </div>
    );
  }

  const { pro, tenant, suite, location, license, insurance } = data;

  // Display fields. Top-level columns are canonical (post Phase 4
  // schema migration, 2026-04-26). legacy_fields fallback kept as a
  // safety net during the cutover soak; remove after Phase 4e drops
  // the column entirely.
  const fullName = [tenant?.first_name ?? pro.first_name, tenant?.last_name ?? pro.last_name]
    .filter(Boolean)
    .join(' ')
    .trim() || pro.email;
  const salonName = tenant?.salon_name ?? readLegacy<string>(tenant, 'salon_name');
  const profession = tenant?.specialty ?? readLegacy<string>(tenant, 'profession');
  const experience = tenant?.tenure ?? readLegacy<string>(tenant, 'tenure');
  const insuranceCarrier = insurance?.carrier ?? readLegacy<string>(tenant, 'ins_carrier');
  const insurancePolicy = insurance?.document_number ?? readLegacy<string>(tenant, 'ins_policy_number');
  const insuranceExpiry = insurance?.expiry_date ?? readLegacy<string>(tenant, 'ins_expiry');
  // License now lives in compliance_records (canonical post Phase 4).
  const licenseType = license?.document_type ?? readLegacy<string>(tenant, 'professional_license_type');
  const licenseNumber = license?.document_number ?? readLegacy<string>(tenant, 'professional_license_number');
  const licenseExpiry = license?.expiry_date ?? readLegacy<string>(tenant, 'license_expiration');
  const emergencyName = tenant?.emergency_contact_name ?? readLegacy<string>(tenant, 'emergency_contact_name');
  const emergencyPhone = tenant?.emergency_contact_phone ?? readLegacy<string>(tenant, 'emergency_contact_phone');
  const emergencyRel = tenant?.emergency_contact_relationship ?? readLegacy<string>(tenant, 'emergency_contact_relationship');
  const insTrim = (s: string | null) => s ? s.split('\n')[0].trim() : null;
  const coiUrl = insurance?.document_url ?? insTrim(readLegacy<string>(tenant, 'coi_document_url'));

  // 958b001e (Cora 2026-10-29): on load, the stored CRM copy in crmMirrorProblem's own words while it is unresolved (the failed
  // warning, or "It will keep trying"); a successful retry or a later successful save clears the stored block (cell 0200)
  // once a save on this page has answered, that card's own CrmNote says it (aws-reviewer's nit: never the same words twice)
  const crmOnLoad = isCell() && !savedHere ? crmMirrorProblem({ zoho: data.crmMirror ?? undefined }) : null;

  return (
    <div className="space-y-3">
      {crmOnLoad && <p role="status" className="card text-[11px] text-amber-700" data-testid="crm-mirror-problem-on-load">{crmOnLoad}</p>}
      {/* Black hero — Profile */}
      <div className="rounded-2xl overflow-hidden bg-image-black text-white p-5 relative">
        {/* subtle pink glow */}
        <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-image-pink/30 blur-3xl pointer-events-none" />
        <div className="relative flex items-start gap-4">
          <ProPhoto
            tenantId={tenant?.id ?? null}
            locationId={tenant?.location_id ?? null}
            photoPath={pro.photo_path}
            photoUrl={pro.photo_url}
            firstName={tenant?.first_name ?? pro.first_name}
            lastName={tenant?.last_name ?? pro.last_name}
            onChanged={() => { void queryClient.invalidateQueries({ queryKey: ['my-info'] }); }}
          />
          {/* THE NAME GETS THE ROOM (measured 2026-09-08, live at 390px). It had 44.5px and its
              text painted straight out of the box across the suite badge — read as a collision,
              though the boxes never overlapped. The arithmetic: the look layer reserves 116px of a
              366px card for its decorative corner, the photo column is 112px because the "Change
              photo" label is wider than the 96px avatar, and three columns cannot share what is
              left. So the badge moves down to the facts, where Suite is a fact like the others,
              and the photo column is held to the avatar. `break-words` is the floor: however
              narrow this gets, the name wraps inside its own box instead of over its neighbour. */}
          <div className="min-w-0 flex-1 break-words">
            <div className="font-bold text-white text-xl leading-tight">{fullName}</div>
            {salonName && <div className="text-sm text-image-pink-soft mt-0.5">{salonName}</div>}
            {profession && <div className="text-xs text-white/60">{profession}</div>}
          </div>
        </div>
        <div className="relative mt-4 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
          {suite && <DarkRow label="Suite" value={String(suite.suite_number)} />}
          <DarkRow label="Email" value={tenant?.email ?? pro.email} wide />
          {tenant?.phone && <DarkRow label="Phone" value={formatPhone(tenant.phone)} />}
          {tenant?.instagram_handle && (
            <DarkRow label="Instagram" value={tenant.instagram_handle.startsWith('@') ? tenant.instagram_handle : `@${tenant.instagram_handle}`} />
          )}
          {experience && <DarkRow label="Experience" value={experience} />}
        </div>
      </div>

      {/* 2-col compliance row — both inline-editable, matches Base44 HubMyInfo */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <LicenseCard
          tenantId={tenant?.id ?? null}
          locationId={tenant?.location_id ?? null}
          licenseType={licenseType}
          licenseNumber={licenseNumber}
          licenseExpiry={licenseExpiry}
          status={license?.verification_status ?? null}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ['my-info'] });
            // A changed Instagram handle must reach Grow IMMEDIATELY (Peter's live bug,
            // 2026-08-29: "Handle was updated in my info, screen did not update") — every
            // Instagram-fed query starts over, so Grow never shows the old handle's error.
            for (const k of ['proIgInsights', 'proIgPosts', 'proIgScan', 'proMarketProfile', 'proMarketMarkets', 'proMarketFeed']) {
              void queryClient.invalidateQueries({ queryKey: [k] });
            }
          }}
        />
        <InsuranceCard
          tenantId={tenant?.id ?? null}
          locationId={tenant?.location_id ?? null}
          carrier={insuranceCarrier}
          policy={insurancePolicy}
          expiry={insuranceExpiry}
          coiUrl={coiUrl}
          status={insurance?.verification_status ?? null}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ['my-info'] });
            // A changed Instagram handle must reach Grow IMMEDIATELY (Peter's live bug,
            // 2026-08-29: "Handle was updated in my info, screen did not update") — every
            // Instagram-fed query starts over, so Grow never shows the old handle's error.
            for (const k of ['proIgInsights', 'proIgPosts', 'proIgScan', 'proMarketProfile', 'proMarketMarkets', 'proMarketFeed']) {
              void queryClient.invalidateQueries({ queryKey: [k] });
            }
          }}
        />
      </div>

      {/* Contact details — editable, writes to the tenant row via the pro-profile-update
          Edge Function, which also mirrors phone / address / Instagram into Zoho. */}
      <ContactCard
        tenantId={tenant?.id ?? null}
        phone={tenant?.phone ?? null}
        homeAddress={tenant?.home_address ?? null}
        instagram={tenant?.instagram_handle ?? null}
        onSaved={() => queryClient.invalidateQueries({ queryKey: ['my-info'] })}
      />

      {/* YOUR PROFILE — the bio and the Main Services (PROS-12).
          Peter, 2026-08-21: "we're bringing everything back into that one My Info section, and
          that's where we are pulling all our information downstream." Both facts came off the
          onboarding form the pro signed and neither was visible to them anywhere until now; both
          are printed on their Pro Spotlight and their door sign. Placed directly under Contact
          Details because this is the public half of the same identity. */}
      <ProfileCard
        tenantId={tenant?.id ?? null}
        bio={tenant?.short_bio ?? null}
        services={tenant?.services_offered ?? null}
        onSaved={() => queryClient.invalidateQueries({ queryKey: ['my-info'] })}
      />

      {/* The pro's dynamic QR code MOVED to the Grow room (Peter, 2026-08-27:
          a marketing asset was filed between the door code and the Wi-Fi
          password, which is why a real pro could not find it). Where Grow is
          not yet switched on, the card stays HERE — no pro loses their QR in
          the transition; the pilot fence decides which home they see. */}
      {!growOn && (
        <QrCodeCard
          proId={pro.id}
          firstName={tenant?.first_name ?? pro.first_name}
          websiteUrl={tenant?.website_url ?? null}
          instagram={tenant?.instagram_handle ?? null}
          businessInstagram={tenant?.business_instagram ?? null}
        />
      )}

      {/* Suite Details, Access Code and Wi-Fi MOVED to My Studio (Two Wings IA,
          Peter 2026-08-27, decision aa4fc89d): a door code is a fact about the
          STUDIO, not the person — Me keeps identity only. The masking behaviour
          travelled with the access-code card; MyStudio.test.tsx pins it there. */}

      {/* Emergency Contact — editable, writes to the tenant row via the pro-profile-update
          Edge Function, which also mirrors it onto the Zoho Tenant_Application record. */}
      <EmergencyCard
        tenantId={tenant?.id ?? null}
        name={emergencyName}
        phone={emergencyPhone}
        relationship={emergencyRel}
        onSaved={() => queryClient.invalidateQueries({ queryKey: ['my-info'] })}
      />

      {/* The pro's spotlight status — their own press, so it stays on Me.
          Ticket history MOVED to My Studio with the Maintenance wing
          (Two Wings IA). Hides itself if there's nothing to show. */}
      <MySpotlightSection proId={pro.id} />

      {/* Quick contact footer — worded and styled like the Documents page's footer
          (feedback 86f4cedd: "Need to update? (617) 314-3772 ??" read as two fragments;
          the established pattern is a full question + "Call or text" + a tappable number). */}
      {location?.phone && (
        <p className="text-center text-xs text-image-charcoal pt-3">
          Need to update anything here? Call or text{' '}
          <a href={`tel:${location.phone}`} className="font-semibold text-image-black hover:text-image-pink">
            {formatPhone(location.phone)}
          </a>
        </p>
      )}
    </div>
  );
}

function DarkRow({ label, value, wide }: { label: string; value: string; wide?: boolean }) {
  return (
    <div className={wide ? 'col-span-2' : ''}>
      <div className="text-[10px] uppercase tracking-wider text-white/40">{label}</div>
      <div className="text-white text-sm break-all">{value}</div>
    </div>
  );
}

function CompactField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-image-slate">{label}</div>
      {/* break-WORDS, not break-ALL. break-all splits at any character, so a real phone number
          rendered as "+1 (617) 555-23 / 67" and a postcode as "020 / 67" — data that looks
          corrupted. break-words only breaks a token that genuinely cannot fit on its own line,
          which still saves a long email from overflowing. Found on a pro's own screen, 2026-08-09. */}
      <div className="text-image-black text-sm break-words">{value}</div>
    </div>
  );
}

/** -------- small presentational helpers -------- */
/* AccessCodeCard and WifiRow MOVED to MyStudio.tsx with the Two Wings IA —
   the masking doctrine (0f346ad2) travelled with them. */

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-image-slate">{label}</span>
      {/* See CompactField: break-all was splitting the studio postcode mid-number. */}
      <span className="text-image-black text-right break-words">{value}</span>
    </div>
  );
}

function SectionHeader({
  icon, accent, title, light,
}: { icon: React.ReactNode; accent: string; title: string; light?: boolean }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={accent}>{icon}</span>
      <h3 className={`ph-sec ${light ? '!text-white' : ''}`}>{title}</h3>
    </div>
  );
}

/** Inline-editable License card — matches Base44 HubMyInfo.jsx.
 *  Exported so the write path it takes is testable: MyInfo.compliance.test.tsx renders this
 *  card directly and asserts the save reaches the Edge Function (and therefore Zoho), which a
 *  browser-side supabase.rpc never could. */
export function LicenseCard({
  tenantId, locationId, licenseType, licenseNumber, licenseExpiry, status, onSaved,
}: {
  tenantId: string | null;
  locationId: string | null;
  status?: string | null;
  licenseType: string | null;
  licenseNumber: string | null;
  licenseExpiry: string | null;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [type, setType] = useState(licenseType ?? '');
  const [num, setNum] = useState(licenseNumber ?? '');
  const [expiry, setExpiry] = useState(licenseExpiry ?? '');

  const valid = !!licenseNumber && !isExpired(licenseExpiry);

  const save = useMutation({
    mutationFn: async () => {
      if (!tenantId || !locationId) throw new Error('Missing tenant context');
      if (!num.trim() || !expiry) throw new Error('Number and expiry are required');
      // THROUGH THE EDGE FUNCTION, NOT supabase.rpc DIRECTLY (2026-08-07).
      //
      // This card used to call pro_add_own_compliance straight from the browser. That saved
      // correctly — the function is SECURITY DEFINER and checks in SQL that the tenant row is
      // the caller's AND at a location they belong to — but it could never reach Zoho, because
      // there is no CRM on the other side of a supabase.rpc. A pro updated their licence here
      // and Zoho went on showing the old one forever.
      //
      // pro-profile-update is now the single write path for everything on My Info. It calls the
      // SAME database function with the SAME caller token, so the boundary is unchanged, and
      // then mirrors the licence to Zoho: Professional_License_Number,
      // Type_of_Professional_License and License_Expiration on Tenant_Application, plus
      // License_Expiration on the Contact so the two cannot disagree about the expiry.
      return await saveProProfile({
        record_type: 'license',
        document_number: num.trim(),
        document_type: type || null,
        expiry_date: expiry,
      }, tenantId);
    },
    onSuccess: () => {
      setEditing(false);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 3000);
      onSaved();
    },
  });

  return (
    <div className="card !p-4">
      <div className="flex items-center justify-between">
        <SectionHeader icon={<RibbonIcon />} accent="text-image-pink" title="License" />
        <div className="flex items-center gap-1.5">
          <StatusBadge status={savedFlash ? 'pending' : (status ?? null)} valid={valid} />
          {!editing && (
            <button
              onClick={() => { setType(licenseType ?? ''); setNum(licenseNumber ?? ''); setExpiry(licenseExpiry ?? ''); setEditing(true); }}
              className="text-[11px] px-2 py-1 rounded border border-image-border text-image-charcoal hover:text-image-pink hover:border-image-pink/40 transition-colors inline-flex items-center gap-1"
            >
              ✎ Edit
            </button>
          )}
        </div>
      </div>

      {editing ? (
        <div className="mt-3 space-y-2.5">
          <Field label="Type">
            <BrandSelect value={type} onChange={setType} ariaLabel="License type"
              placeholder="Select type" options={LICENSE_TYPES} />
          </Field>
          <Field label="Number">
            <input className="input" value={num} onChange={e => setNum(e.target.value)} placeholder="License number" />
          </Field>
          <Field label="Expiration">
            <input type="date" className="input" value={expiry} onChange={e => setExpiry(e.target.value)} />
          </Field>
          {save.isError && (
            <div className="text-[11px] text-red-600">{(save.error as Error).message}</div>
          )}
          <div className="flex gap-2 pt-1">
            <button
              onClick={() => save.mutate()}
              disabled={save.isPending || !num || !expiry}
              className="flex-1 px-3 py-2 rounded-lg bg-image-pink text-white text-sm font-semibold hover:bg-image-plum disabled:opacity-40 transition-colors"
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
            <button onClick={() => setEditing(false)} className="px-3 py-2 rounded-lg border border-image-border text-sm text-image-charcoal hover:text-image-black">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-2 text-xs space-y-1.5">
          <Row label="Type" value={licenseType ?? '—'} />
          <Row label="Number" value={licenseNumber ?? '—'} />
          <Row label="Expires" value={formatDate(licenseExpiry)} />
          {savedFlash && !crmMirrorProblem(save.data) && <p className="text-[11px] text-emerald-600 pt-1">✓ License updated successfully</p>}
          <CrmNote m={save} editing={editing} />
          {!licenseNumber && !savedFlash && (
            <p className="text-[11px] text-image-charcoal pt-1">No license on file. Click Edit to add it.</p>
          )}
        </div>
      )}
    </div>
  );
}

/** Insurance/COI card — inline-editable carrier / policy / expiry, mirroring the License
 *  card. It writes to `compliance_records` (record_type 'insurance') — the SAME table the
 *  page reads insurance from — so an edit surfaces immediately (no dead control). The COI
 *  *document* itself is still uploaded via /forms/coi. */
export function InsuranceCard({
  tenantId, locationId, carrier, policy, expiry, coiUrl, status, onSaved,
}: {
  tenantId: string | null;
  locationId: string | null;
  status?: string | null;
  carrier: string | null;
  policy: string | null;
  expiry: string | null;
  coiUrl: string | null;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [carrierV, setCarrierV] = useState(carrier ?? '');
  const [policyV, setPolicyV] = useState(policy ?? '');
  const [expiryV, setExpiryV] = useState(expiry ?? '');
  const valid = !!policy && !isExpired(expiry);

  const save = useMutation({
    mutationFn: async () => {
      if (!tenantId || !locationId) throw new Error('Missing tenant context');
      if (!policyV.trim() || !expiryV) throw new Error('Policy number and expiry are required');
      // Same single write path as the License card (see there for why a direct supabase.rpc
      // could never reach Zoho). The Edge Function calls the same pro-scoped database function
      // and then mirrors the COI to Zoho: Insurance_Company, Insurance_Policy_Number and
      // Insurance_Expiration on Tenant_Onboarding, plus Insurance_Expiration on the Contact.
      return await saveProProfile({
        record_type: 'insurance',
        document_number: policyV.trim(),
        carrier: carrierV.trim() || null,
        expiry_date: expiryV,
      }, tenantId);
    },
    onSuccess: () => {
      setEditing(false);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 3000);
      onSaved();
    },
  });

  return (
    <div className="card !p-4">
      <div className="flex items-center justify-between">
        <SectionHeader icon={<ShieldIcon />} accent="text-image-pink" title="Insurance" />
        <div className="flex items-center gap-1.5">
          <StatusBadge status={savedFlash ? 'pending' : (status ?? null)} valid={valid} />
          {!editing && (
            <button
              onClick={() => { setCarrierV(carrier ?? ''); setPolicyV(policy ?? ''); setExpiryV(expiry ?? ''); setEditing(true); }}
              className="text-[11px] px-2 py-1 rounded border border-image-border text-image-charcoal hover:text-image-pink hover:border-image-pink/40 transition-colors inline-flex items-center gap-1"
            >
              ✎ Edit
            </button>
          )}
        </div>
      </div>

      {editing ? (
        <div className="mt-3 space-y-2.5">
          <Field label="Carrier">
            <input className="input" value={carrierV} onChange={e => setCarrierV(e.target.value)} placeholder="Insurance carrier" />
          </Field>
          <Field label="Policy #">
            <input className="input" value={policyV} onChange={e => setPolicyV(e.target.value)} placeholder="Policy number" />
          </Field>
          <Field label="Expiration">
            <input type="date" className="input" value={expiryV} onChange={e => setExpiryV(e.target.value)} />
          </Field>
          {save.isError && (
            <div className="text-[11px] text-red-600">{(save.error as Error).message}</div>
          )}
          <div className="flex gap-2 pt-1">
            <button
              onClick={() => save.mutate()}
              disabled={save.isPending || !policyV.trim() || !expiryV}
              className="flex-1 px-3 py-2 rounded-lg bg-image-pink text-white text-sm font-semibold hover:bg-image-plum disabled:opacity-40 transition-colors"
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
            <button onClick={() => setEditing(false)} className="px-3 py-2 rounded-lg border border-image-border text-sm text-image-charcoal hover:text-image-black">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="mt-2 text-xs space-y-1.5">
            <Row label="Carrier" value={carrier ?? '—'} />
            <Row label="Policy" value={policy ?? '—'} />
            <Row label="Expires" value={formatDate(expiry)} />
            {savedFlash && !crmMirrorProblem(save.data) && <p className="text-[11px] text-emerald-600 pt-1">✓ Insurance updated successfully</p>}
            <CrmNote m={save} editing={editing} />
          </div>
          <div className="mt-3 pt-3 border-t border-image-border space-y-1.5">
            {!carrier ? (
              <Link
                to="/forms/coi"
                className="flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-lg bg-image-pink hover:bg-image-plum text-white text-sm font-semibold transition-colors"
              >
                🛡 Upload Your COI
              </Link>
            ) : (
              <>
                {coiUrl && <ViewCoiLink url={coiUrl} />}
                <Link to="/forms/coi" className="block text-[11px] text-image-charcoal hover:text-image-pink text-center transition-colors">
                  ↗ Update or upload a new COI
                </Link>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/** Re-usable form Field used by both the License inline form and elsewhere */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-image-charcoal font-semibold mb-1">{label}</label>
      {children}
    </div>
  );
}

/** The badge reflects the RECORD'S REAL STATUS, not just "has a number and isn't expired".
 *  A pro files a CLAIM (verification_status 'pending'); only an operator marks it verified
 *  (Peter, 2026-08-07: "pending review"). Showing a green ✓ Valid on a pending record would be
 *  the screen self-certifying on the pro's behalf. */
function StatusBadge({ status, valid }: { status: string | null; valid: boolean }) {
  // COMPLIANCE-0: the database admits pending | verified | rejected | not_required; a filing
  // waiting on the owner is 'pending' and nothing else now.
  if (status === 'pending') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
        ⏳ Pending review
      </span>
    );
  }
  if (status === 'rejected') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">
        ✕ Needs attention
      </span>
    );
  }
  if (status === 'not_required') {
    // The studio has said this pro does not need one. Not a gap, and not "Valid" either.
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-50 text-gray-600 border border-gray-200">
        Not required
      </span>
    );
  }
  return <Badge valid={valid} />;
}

function Badge({ valid }: { valid: boolean }) {
  return valid ? (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">
      ✓ Valid
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700">
      ⚠ Missing
    </span>
  );
}

/** Diff helper: include a field in the patch only when the trimmed value changed. */
function changedField(
  patch: Record<string, string | null>,
  key: string,
  next: string,
  original: string | null,
) {
  if (next.trim() !== (original ?? '').trim()) {
    patch[key] = next; // '' = the pro erased it: the EF sends it as a clear (HUB-029), never as keep
  }
}

/** Inline-editable Contact card — phone, home address, Instagram. Mirrors the
 *  License card's edit UX exactly (Edit toggle → fields → Save/Cancel). */
function ContactCard({
  tenantId, phone, homeAddress, instagram, onSaved,
}: {
  tenantId: string | null;
  phone: string | null;
  homeAddress: string | null;
  instagram: string | null;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [phoneV, setPhoneV] = useState(phone ?? '');
  const [addrV, setAddrV] = useState(homeAddress ?? '');
  const [igV, setIgV] = useState(instagram ?? '');

  const save = useMutation({
    mutationFn: async () => {
      const patch: Record<string, string | null> = {};
      changedField(patch, 'phone', phoneV, phone);
      changedField(patch, 'home_address', addrV, homeAddress);
      changedField(patch, 'instagram_handle', igV, instagram);
      if (Object.keys(patch).length === 0) return; // nothing changed — no-op
      return await saveProProfile(patch, tenantId);
    },
    onSuccess: () => {
      setEditing(false);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 3000);
      onSaved();
    },
  });

  return (
    <div className="card !p-4">
      <div className="flex items-center justify-between">
        <SectionHeader icon={<UserIcon />} accent="text-image-pink" title="Contact Details" />
        {!editing && (
          <button
            onClick={() => { setPhoneV(phone ?? ''); setAddrV(homeAddress ?? ''); setIgV(instagram ?? ''); setEditing(true); }}
            className="text-[11px] px-2 py-1 rounded border border-image-border text-image-charcoal hover:text-image-pink hover:border-image-pink/40 transition-colors inline-flex items-center gap-1"
          >
            ✎ Edit
          </button>
        )}
      </div>

      {editing ? (
        <div className="mt-3 space-y-2.5">
          <Field label="Phone">
            <input className="input" value={phoneV} onChange={e => setPhoneV(e.target.value)} placeholder="(555) 555-5555" inputMode="tel" />
          </Field>
          <Field label="Home Address">
            <input className="input" value={addrV} onChange={e => setAddrV(e.target.value)} placeholder="Street, City, State ZIP" />
          </Field>
          <Field label="Instagram">
            <input className="input" value={igV} onChange={e => setIgV(e.target.value)} placeholder="@handle" />
          </Field>
          {save.isError && (
            <div className="text-[11px] text-red-600">{(save.error as Error).message}</div>
          )}
          <div className="flex gap-2 pt-1">
            <button
              onClick={() => save.mutate()}
              disabled={save.isPending}
              className="flex-1 px-3 py-2 rounded-lg bg-image-pink text-white text-sm font-semibold hover:bg-image-plum disabled:opacity-40 transition-colors"
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
            <button onClick={() => setEditing(false)} className="px-3 py-2 rounded-lg border border-image-border text-sm text-image-charcoal hover:text-image-black">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-2 text-xs space-y-1.5">
          <Row label="Phone" value={phone ? formatPhone(phone) : '—'} />
          <Row label="Home Address" value={homeAddress ?? '—'} />
          <Row label="Instagram" value={instagram ? (instagram.startsWith('@') ? instagram : `@${instagram}`) : '—'} />
          <CrmNote m={save} editing={editing} />
          {savedFlash && !crmMirrorProblem(save.data) && (
            <p className="text-[11px] text-emerald-600 pt-1">
              ✓ Contact details updated{igV !== (instagram ?? '') || igV ? (
                <> · <Link to="/grow" className="font-bold text-image-pink hover:underline">Back to Grow to pull your numbers</Link></>
              ) : null}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * YOUR PROFILE — the bio and up to four Main Services (PROS-12).
 *
 * Peter, 2026-08-21: "as part of the onboarding documentation, they filled out a bio section…
 * in my information we should bring that bio back. That is where a pro doing the orientation
 * can say, here\'s what you filled out, here\'s what we have on you." And the services: "they
 * already checked all their services. We don\'t have to go through a bio to see what their
 * services are."
 *
 * Everything else on this screen is what the STUDIO needs from the pro — licence, insurance,
 * emergency contact. This is the half the pro shows the WORLD, and it is the half that every
 * printed design actually renders. Both values were sitting in the signed onboarding PDF in Zoho
 * the whole time; neither was visible to the pro, and neither could be edited by them, which is
 * how a Curl Specialist ended up with an esthetician\'s service menu on her spotlight.
 *
 * Four slots rather than one free-text box, because four is what fits on a sign and what the
 * owner has always hand-picked. They are joined with ", " into the one column every surface
 * reads, so there is no second representation to keep in step.
 */
const SERVICE_SLOTS = 4;

export function ProfileCard({
  tenantId, bio, services, onSaved,
}: {
  tenantId: string | null;
  bio: string | null;
  services: string | null;
  onSaved: () => void;
}) {
  const splitServices = (v: string | null) => {
    const parts = (v ?? '').split(',').map(x => x.trim()).filter(Boolean).slice(0, SERVICE_SLOTS);
    return [...parts, ...Array(SERVICE_SLOTS - parts.length).fill('')];
  };
  const [editing, setEditing] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [bioV, setBioV] = useState(bio ?? '');
  const [svcV, setSvcV] = useState<string[]>(splitServices(services));

  const reset = () => { setBioV(bio ?? ''); setSvcV(splitServices(services)); };
  const shown = (services ?? '').split(',').map(x => x.trim()).filter(Boolean);

  const save = useMutation({
    mutationFn: async () => {
      const patch: Record<string, string | null> = {};
      changedField(patch, 'short_bio', bioV, bio);
      // Blank slots collapse out, so clearing every one sends '' — which the Edge Function maps
      // to null. That is a real edit: a pro removing their services means the sign prints none,
      // not that it keeps the last set forever.
      changedField(patch, 'services_offered', svcV.map(s => s.trim()).filter(Boolean).join(', '), services);
      if (Object.keys(patch).length === 0) return;
      return await saveProProfile(patch, tenantId);
    },
    onSuccess: () => {
      setEditing(false);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 3000);
      onSaved();
    },
  });

  return (
    <div className="card !p-4">
      <div className="flex items-center justify-between">
        <SectionHeader icon={<UserIcon />} accent="text-image-pink" title="Your Profile" />
        {!editing && (
          <button
            onClick={() => { reset(); setEditing(true); }}
            className="text-[11px] px-2 py-1 rounded border border-image-border text-image-charcoal hover:text-image-pink hover:border-image-pink/40 transition-colors inline-flex items-center gap-1"
          >
            ✎ Edit
          </button>
        )}
      </div>

      <p className="mt-1 text-[11px] text-image-charcoal">
        This is what your studio prints and posts about you — your spotlight, your door sign.
        Change it any time.
      </p>

      {editing ? (
        <div className="mt-3 space-y-2.5">
          <Field label="Main Services">
            <div className="space-y-1.5">
              {svcV.map((v, i) => (
                <input
                  key={i}
                  className="input"
                  value={v}
                  onChange={e => setSvcV(prev => prev.map((x, j) => (j === i ? e.target.value : x)))}
                  placeholder={`Service ${i + 1}`}
                  aria-label={`Main service ${i + 1}`}
                />
              ))}
            </div>
          </Field>
          <Field label="About You">
            <textarea
              className="input min-h-[7rem]"
              value={bioV}
              onChange={e => setBioV(e.target.value)}
              placeholder="A few sentences about your work — this is what your studio shares when they feature you."
            />
          </Field>
          {save.isError && (
            <div className="text-[11px] text-red-600">{(save.error as Error).message}</div>
          )}
          <div className="flex gap-2 pt-1">
            <button
              onClick={() => save.mutate()}
              disabled={save.isPending}
              className="flex-1 px-3 py-2 rounded-lg bg-image-pink text-white text-sm font-semibold hover:bg-image-plum disabled:opacity-40 transition-colors"
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
            <button onClick={() => setEditing(false)} className="px-3 py-2 rounded-lg border border-image-border text-sm text-image-charcoal hover:text-image-black">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-2.5 text-xs space-y-2.5">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-image-charcoal">Main Services</div>
            {shown.length ? (
              <div className="mt-1 flex flex-wrap gap-1.5">
                {shown.map(s => (
                  <span key={s} className="px-2 py-0.5 rounded-full bg-image-pink/10 text-image-plum text-[11px] font-semibold">
                    {s}
                  </span>
                ))}
              </div>
            ) : (
              // Never a bare dash here: this is the one field whose emptiness the pro can fix,
              // and the sign prints nothing until they do.
              <p className="mt-1 text-image-charcoal">
                Nothing set yet — add up to four and they appear on your spotlight.
              </p>
            )}
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-image-charcoal">About You</div>
            <p className="mt-1 whitespace-pre-wrap text-image-black">
              {bio?.trim() || 'Nothing written yet — tell your studio about your work.'}
            </p>
          </div>
          {savedFlash && !crmMirrorProblem(save.data) && <p className="text-[11px] text-emerald-600">✓ Your profile is updated</p>}
          <CrmNote m={save} editing={editing} />
        </div>
      )}
    </div>
  );
}

/** Inline-editable Emergency Contact card — name, phone, relationship.
 *  Mirrors the License card's edit UX. */
function EmergencyCard({
  tenantId, name, phone, relationship, onSaved,
}: {
  tenantId: string | null;
  name: string | null;
  phone: string | null;
  relationship: string | null;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [nameV, setNameV] = useState(name ?? '');
  const [phoneV, setPhoneV] = useState(phone ?? '');
  const [relV, setRelV] = useState(relationship ?? '');

  const save = useMutation({
    mutationFn: async () => {
      const patch: Record<string, string | null> = {};
      changedField(patch, 'emergency_contact_name', nameV, name);
      changedField(patch, 'emergency_contact_phone', phoneV, phone);
      changedField(patch, 'emergency_contact_relationship', relV, relationship);
      if (Object.keys(patch).length === 0) return;
      return await saveProProfile(patch, tenantId);
    },
    onSuccess: () => {
      setEditing(false);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 3000);
      onSaved();
    },
  });

  return (
    <div className="card !p-4">
      <div className="flex items-center justify-between">
        <SectionHeader icon={<PhoneIcon />} accent="text-image-pink" title="Emergency Contact" />
        {!editing && (
          <button
            onClick={() => { setNameV(name ?? ''); setPhoneV(phone ?? ''); setRelV(relationship ?? ''); setEditing(true); }}
            className="text-[11px] px-2 py-1 rounded border border-image-border text-image-charcoal hover:text-image-pink hover:border-image-pink/40 transition-colors inline-flex items-center gap-1"
          >
            ✎ Edit
          </button>
        )}
      </div>

      {editing ? (
        <div className="mt-3 space-y-2.5">
          <Field label="Name">
            <input className="input" value={nameV} onChange={e => setNameV(e.target.value)} placeholder="Full name" />
          </Field>
          <Field label="Phone">
            <input className="input" value={phoneV} onChange={e => setPhoneV(e.target.value)} placeholder="(555) 555-5555" inputMode="tel" />
          </Field>
          <Field label="Relationship">
            <input className="input" value={relV} onChange={e => setRelV(e.target.value)} placeholder="e.g. Spouse, Parent" />
          </Field>
          {save.isError && (
            <div className="text-[11px] text-red-600">{(save.error as Error).message}</div>
          )}
          <div className="flex gap-2 pt-1">
            <button
              onClick={() => save.mutate()}
              disabled={save.isPending}
              className="flex-1 px-3 py-2 rounded-lg bg-image-pink text-white text-sm font-semibold hover:bg-image-plum disabled:opacity-40 transition-colors"
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
            <button onClick={() => setEditing(false)} className="px-3 py-2 rounded-lg border border-image-border text-sm text-image-charcoal hover:text-image-black">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-2 grid grid-cols-3 gap-3 text-xs">
          <CompactField label="Name" value={name ?? '—'} />
          <CompactField label="Phone" value={phone ? formatPhone(phone) : '—'} />
          <CompactField label="Relationship" value={relationship ?? '—'} />
          {savedFlash && !crmMirrorProblem(save.data) && <p className="col-span-3 text-[11px] text-emerald-600 pt-1">✓ Emergency contact updated</p>}
          <div className="col-span-3"><CrmNote m={save} editing={editing} /></div>
        </div>
      )}
    </div>
  );
}

/** -------- inline icons (no extra deps) -------- */

function UserIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function RibbonIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="6" /><path d="M15.5 13.5 17 22l-5-3-5 3 1.5-8.5" />
    </svg>
  );
}
function ShieldIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}
function PhoneIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92V21a1 1 0 0 1-1.11 1A19 19 0 0 1 2 4.11 1 1 0 0 1 3 3h4.09a1 1 0 0 1 1 .75l1 4a1 1 0 0 1-.27 1L7 10.5a16 16 0 0 0 6.5 6.5l1.75-1.75a1 1 0 0 1 1-.27l4 1a1 1 0 0 1 .75 1Z" />
    </svg>
  );
}

/* ============================================================
 * MyTicketsSection — full ticket history (renders on My Studio since the
 * Two Wings IA, 2026-08-27)
 *
 * The "Maintenance" row is the *new submission* path; this
 * section is the *history* path. Shows every ticket the Pro has
 * ever filed: active ones with a pulsing pink dot at the top,
 * closed ones with calm green pills below. Realtime-subscribed,
 * so when the operator resolves a ticket on iOS the row
 * transitions visually on this page within a couple of seconds.
 *
 * Hides itself entirely if the Pro has never filed a ticket.
 * Per ADR-022, status surfaces where the Pro has visibility
 * into their own stuff — but actions stay minimal.
 * ============================================================ */

type TicketRow = {
  id: string;
  /** FACILITY-4 · who took it (a vendor or the owner's own person answered TAKE), and when. */
  acknowledged_name?: string | null;
  acknowledged_at?: string | null;
  ticket_number: string;
  title: string | null;
  category: string | null;
  description: string | null;
  priority: string;
  status: string;
  created_at: string;
  resolved_at: string | null;
  closed_at: string | null;
};

type TicketEventRow = {
  id: string;
  ticket_id: string;
  event_type: string;
  actor_name: string | null;
  from_value: string | null;
  to_value: string | null;
  comment: string | null;
  created_at: string;
};

const ACTIVE_TICKET_STATUSES = ['open', 'pending', 'in_progress', 'on_hold', 'scheduled'];
const CLOSED_TICKET_STATUSES = ['resolved', 'closed', 'cancelled'];

/** Rendered by MY STUDIO since the Two Wings IA (2026-08-27) — the code
 *  stays here beside its siblings; the wing imports it. */
export function MyTicketsSection({ proId }: { proId: string }) {
  // MT fix 2026-07-11: scope to the active location — a Pro at two
  // studios saw both ticket queues merged here regardless of the
  // location picker.
  const { activeLocationId } = useActiveLocationId();
  const [showAllClosed, setShowAllClosed] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery<TicketRow[]>({
    queryKey: ['my-info-tickets', proId, activeLocationId],
    queryFn: async () => {
      if (isCell()) {
        // CELL (M28): GET /v1/pro-hub/pros/{proId}/tickets?location= — the same select, filter and order.
        const { data, error } = await cellGet<{ rows: TicketRow[] }>(`/v1/pro-hub/pros/${proId}/tickets`, { location: activeLocationId });
        if (error) throw error;
        return data?.rows ?? [];
      }
      let q = supabase
        .from('tickets')
        .select('id, ticket_number, title, category, description, priority, status, created_at, resolved_at, closed_at, acknowledged_name, acknowledged_at')
        .eq('pro_id', proId);
      if (activeLocationId) q = q.eq('location_id', activeLocationId);
      const { data, error } = await q.order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  // Realtime: when operator resolves a ticket on iOS or anyone
  // updates a row, refetch so this section reflects it without a
  // manual page refresh. Home tile badge also depends on this — keep
  // both keys in sync from the single event.
  useRealtimeTable({
    table: 'tickets',
    filter: `pro_id=eq.${proId}`,
    invalidateKeys: [['my-info-tickets', proId], ['hub-home']],
    enabled: !!proId,
  });

  // HUB-074: a failed read used to render nothing at all — as if the pro had never filed a ticket.
  if (isError && !data) {
    return (
      <section className="card !p-3 text-xs text-red-700" role="alert" data-testid="tickets-load-failed">
        Your maintenance tickets could not be loaded just now.{' '}
        <button type="button" className="underline" onClick={() => { void refetch(); }}>Try again</button>
      </section>
    );
  }
  if (isLoading || !data || data.length === 0) {
    return null;
  }

  const active = data.filter(t => ACTIVE_TICKET_STATUSES.includes(t.status));
  const closed = data.filter(t => CLOSED_TICKET_STATUSES.includes(t.status));

  // Active tickets always render in full (they need attention).
  // Closed tickets collapse to the most-recent when there's >1, with
  // an expand toggle. Keeps the section compact for Pros with long
  // history while still surfacing the latest closure (e.g. the one
  // the operator just resolved on iOS).
  const visibleClosed = showAllClosed ? closed : closed.slice(0, 1);
  const hiddenClosed = closed.length - visibleClosed.length;

  return (
    <section className="space-y-2">
      <div className="flex items-baseline justify-between px-1">
        <h2 className="text-[11px] font-bold uppercase tracking-[0.15em] text-image-charcoal">
          Maintenance Tickets
        </h2>
        <span className="text-[11px] text-image-charcoal">
          {active.length > 0 && <span className="text-image-pink font-semibold">{active.length} active</span>}
          {active.length > 0 && closed.length > 0 && <span> · </span>}
          {closed.length > 0 && <span>{closed.length} closed</span>}
        </span>
      </div>

      {active.map(t => <TicketRowCard key={t.id} t={t} active />)}
      {visibleClosed.map(t => <TicketRowCard key={t.id} t={t} active={false} />)}

      {hiddenClosed > 0 && (
        <button
          type="button"
          onClick={() => setShowAllClosed(true)}
          className="w-full text-center text-xs text-image-pink hover:text-image-plum font-semibold py-2"
        >
          Show {hiddenClosed} more closed ticket{hiddenClosed > 1 ? 's' : ''} ↓
        </button>
      )}
      {showAllClosed && closed.length > 1 && (
        <button
          type="button"
          onClick={() => setShowAllClosed(false)}
          className="w-full text-center text-xs text-image-charcoal hover:text-image-black font-semibold py-2"
        >
          Show less ↑
        </button>
      )}
    </section>
  );
}

function ProStatusLine({ t }: { t: TicketRow }) {
  return <p className="text-xs font-semibold text-image-plum mt-1.5">{proStatusLine(t)}</p>;
}

function TicketRowCard({ t, active }: { t: TicketRow; active: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const statusLabel = labelTicketStatus(t.status);
  const closedDate = t.resolved_at ?? t.closed_at;

  return (
    <article
      className={`rounded-2xl border p-4 ${
        active
          ? 'border-image-pink/30 bg-image-pink-soft/10'
          : 'border-image-border bg-white opacity-90'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span
              className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full border ${
                active
                  ? 'bg-image-pink-soft/40 text-image-plum border-image-pink/30'
                  : 'bg-emerald-50 text-emerald-700 border-emerald-200'
              }`}
            >
              {active && <span className="size-1.5 rounded-full bg-image-pink animate-pulse" aria-hidden />}
              {statusLabel}
            </span>
            <span className="text-[11px] font-mono text-image-charcoal">{t.ticket_number}</span>
            <span className="inline-flex items-center gap-1.5 text-[11px] text-image-charcoal">
              <span className={`inline-block size-2 rounded-full ${priorityDotClass(t.priority)}`} aria-hidden />
              {/* Priorities are slugs since FACILITY-1 ("emergency"); older rows carry a word. */}
              {t.priority ? t.priority.charAt(0).toUpperCase() + t.priority.slice(1) : ''}
            </span>
          </div>
          <div className="font-semibold text-image-black text-sm leading-snug">
            {t.title ?? t.category ?? 'Maintenance request'}
          </div>
          {t.description && (
            <p className="text-xs text-image-charcoal mt-1 line-clamp-2 leading-relaxed">
              {t.description}
            </p>
          )}
          {active && <ProStatusLine t={t} />}
          <div className="text-[11px] text-image-charcoal mt-1.5">
            Submitted {formatRelative(t.created_at)}
            {closedDate && (
              <>
                {' · '}
                <span className="text-emerald-700">{statusLabel} {formatRelative(closedDate)}</span>
              </>
            )}
          </div>
        </div>
      </div>

      <TicketActivityPanel
        ticketId={t.id}
        expanded={expanded}
        onToggle={() => setExpanded(v => !v)}
      />
    </article>
  );
}

/**
 * Per-ticket activity log. Renders ticket_events rows (operator
 * comments + status-change audit) under each ticket in MyTicketsSection.
 *
 * Collapsed by default to keep the section compact; the header always
 * shows the count so a Pro knows there's something to expand without
 * having to click. Realtime-subscribed via filter ticket_id=eq.<id>
 * so a comment added by the operator on iOS shows up within ~1s
 * without a page refresh.
 *
 * Reads rely on the pros_read_own_ticket_events RLS policy (WI-116
 * in image-studios-backend) — pre-existing authed_read_ticket_events
 * is operator-only via app_can_read_location and would return zero
 * rows for a pure-Pro session.
 *
 * v1 scope: comment + status_changed event types render with copy.
 * created/assigned/resolved fall through to a generic line so we
 * don't silently drop events. Photos in comments are out of scope.
 */
function TicketActivityPanel({
  ticketId, expanded, onToggle,
}: {
  ticketId: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { data, isLoading, isError, refetch } = useQuery<TicketEventRow[]>({
    queryKey: ['my-info-ticket-events', ticketId],
    queryFn: async () => {
      if (isCell()) {
        // CELL (M28): GET /v1/pro-hub/tickets/{ticketId}/events — the same select and order.
        const { data, error } = await cellGet<{ rows: TicketEventRow[] }>(`/v1/pro-hub/tickets/${ticketId}/events`);
        if (error) throw error;
        return data?.rows ?? [];
      }
      const { data, error } = await supabase
        .from('ticket_events')
        .select('id, ticket_id, event_type, actor_name, from_value, to_value, comment, created_at')
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  // Realtime: operator adds a comment on iOS / resolves the ticket →
  // ticket_events INSERT → this panel refetches and the new entry
  // animates in. Hook subscribes unconditionally (cheap channel; RLS
  // gates payload) so the count updates even when the panel is collapsed.
  useRealtimeTable({
    table: 'ticket_events',
    filter: `ticket_id=eq.${ticketId}`,
    invalidateKeys: [['my-info-ticket-events', ticketId]],
  });

  const events = data ?? [];
  const count = events.length;

  return (
    <div className="mt-3 pt-3 border-t border-image-border/60">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between text-[11px] font-semibold uppercase tracking-[0.1em] text-image-charcoal hover:text-image-pink transition-colors"
        aria-expanded={expanded}
      >
        <span>Activity ({isError ? '?' : count})</span>
        <span aria-hidden className="text-image-charcoal/60">{expanded ? '▴' : '▾'}</span>
      </button>

      {expanded && (
        <div className="mt-2.5 space-y-2">
          {isLoading ? (
            <p className="text-[11px] text-image-charcoal">Loading…</p>
          ) : isError ? (
            // HUB-074: never "awaiting first response" when the history simply could not be read.
            <p className="text-[11px] text-red-700" role="alert" data-testid="ticket-activity-failed">
              This ticket&rsquo;s activity could not be loaded.{' '}
              <button type="button" className="underline" onClick={() => { void refetch(); }}>Try again</button>
            </p>
          ) : count === 0 ? (
            <p className="text-[11px] text-image-charcoal italic">
              Submitted · awaiting first response from the operations team
            </p>
          ) : (
            events.map(ev => <TicketEventLine key={ev.id} ev={ev} />)
          )}
        </div>
      )}
    </div>
  );
}

function TicketEventLine({ ev }: { ev: TicketEventRow }) {
  const when = formatRelative(ev.created_at);
  const who  = ev.actor_name?.trim() || 'Operator';

  if (ev.event_type === 'commented' && ev.comment) {
    return (
      <div className="text-xs">
        <div className="text-image-black leading-relaxed whitespace-pre-wrap">{ev.comment}</div>
        <div className="text-[11px] text-image-charcoal mt-0.5">{who} · {when}</div>
      </div>
    );
  }

  // status_changed (canonical) + legacy 'resolved' rows (older schema
  // wrote event_type='resolved' with from='open' to='resolved' before
  // the status_changed convention was adopted).
  if ((ev.event_type === 'status_changed' || ev.event_type === 'resolved') && ev.to_value) {
    return (
      <div className="text-[11px] text-image-charcoal">
        {/* A STATEMENT OF WHAT CHANGED, not a link — but the glyph reads as a
            link arrow to anyone scanning, so the change is spelled instead. */}
        Status: <span className="text-image-black">{labelTicketStatus(ev.from_value ?? '—')}</span>
        {' to '}
        <span className="text-image-black font-medium">{labelTicketStatus(ev.to_value)}</span>
        {' · '}{when} by {who}
      </div>
    );
  }

  // created / assigned / unknown — keep the row visible so Pros aren't
  // confused by a count that doesn't match what's rendered.
  return (
    <div className="text-[11px] text-image-charcoal">
      {labelEventType(ev.event_type)}
      {ev.comment ? <span className="text-image-black"> — {ev.comment}</span> : null}
      {' · '}{when} by {who}
    </div>
  );
}

function labelEventType(t: string): string {
  return ({
    created: 'Ticket created',
    commented: 'Comment',
    status_changed: 'Status changed',
    assigned: 'Assigned',
    resolved: 'Resolved',
  } as Record<string, string>)[t] ?? t;
}

function labelTicketStatus(s: string): string {
  return ({
    open: 'Open',
    pending: 'Pending',
    in_progress: 'In Progress',
    on_hold: 'On Hold',
    scheduled: 'Scheduled',
    resolved: 'Resolved',
    closed: 'Closed',
    cancelled: 'Cancelled',
  } as Record<string, string>)[s] ?? s;
}

function priorityDotClass(p: string): string {
  return ({
    Emergency: 'bg-red-500',
    High: 'bg-orange-500',
    Medium: 'bg-yellow-500',
    Low: 'bg-emerald-500',
  } as Record<string, string>)[p] ?? 'bg-gray-400';
}

function formatRelative(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() === now.getFullYear() ? undefined : 'numeric',
  });
}

/* ============================================================
 * MySpotlightSection — compact "your spotlight status" card
 *
 * 5-state lifecycle per ADR-018:
 *   submitted → drafted → pro_review → approved → posted
 *
 * Surfaces clearly when the Pro has an action to take
 * (pro_review = "approve your draft"), otherwise just shows the
 * current state without making the Pro feel they need to do
 * anything. Per ADR-022, status timelines surface only where
 * the Pro can act.
 * ============================================================ */

type SpotlightRow = {
  id: string;
  status: string;
  draft_post: string | null;
  posted_url: string | null;
  created_at: string;
  /**
   * The spotlight's own photos. Peter, 2026-08-18: "the pro spotlight section on the
   * Pro Hub does not have the picture" — and it never did. This card selected status
   * and text only, so there was nothing to render and nothing failing to sync. Slot 0
   * IS the photo everywhere in the Hub (see the Spotlight editor).
   */
  photo_urls: string[] | null;
  /** When the pro approved it — the date the "approved" card names, because "publishing soon"
   *  was still on Aria Patel's card 26 days after approval (Phil, feedback 026cb350). */
  pro_approved_at: string | null;
};

function MySpotlightSection({ proId }: { proId: string }) {
  const { activeLocationId } = useActiveLocationId();
  const { data, isLoading } = useQuery<SpotlightRow | null>({
    queryKey: ['my-info-spotlight', proId, activeLocationId],
    queryFn: async () => {
      if (isCell()) {
        // CELL (M28): GET /v1/pro-hub/pros/{proId}/spotlight?location= — the same select, filter, order and limit.
        const { data, error } = await cellGet<{ spotlight: SpotlightRow | null }>(`/v1/pro-hub/pros/${proId}/spotlight`, { location: activeLocationId });
        if (error) throw error;
        return data?.spotlight ?? null;
      }
      let q = supabase
        .from('spotlights')
        .select('id, status, draft_post, posted_url, created_at, photo_urls, pro_approved_at')
        .eq('pro_id', proId);
      if (activeLocationId) q = q.eq('location_id', activeLocationId);
      const { data, error } = await q
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle<SpotlightRow>();
      if (error) throw error;
      return data;
    },
  });

  // Realtime: status transitions on the Pro's spotlight (Owner edited
  // → pro_review → approved → posted) drive this card's headline and
  // dot color. Also invalidate ['hub-home'] so the tile badge tracks.
  useRealtimeTable({
    table: 'spotlights',
    filter: `pro_id=eq.${proId}`,
    invalidateKeys: [['my-info-spotlight', proId], ['hub-home']],
    enabled: !!proId,
  });

  // Slot 0 is THE photo, the same rule the Spotlight editor follows. Resolved before the early
  // return (it is a hook): a spotlight-photos address is signed for display (#53 makes that
  // bucket private), a /m/ address is re-rooted on the media host and otherwise left alone.
  const photo = useDisplayPhotoUrl(data?.photo_urls?.[0] ?? null);

  if (isLoading || !data) return null;

  const meta = spotlightMeta(data.status, data.pro_approved_at);
  const isExternal = data.status === 'posted' && !!data.posted_url;
  // Only render a CTA arrow when there's somewhere meaningful to go:
  //   • pro_review → "Review & Approve →"   (Pro must act)
  //   • posted     → "View live ↗"          (external Instagram/Facebook link)
  // Every other state (submitted, drafted, approved, draft_failed) is
  // passive — the Pro doesn't need to navigate anywhere; the card
  // itself is the status surface. Suppressing the arrow keeps the
  // card honest about whether it's actionable.
  const linkable = meta.actionable || isExternal;
  const ctaText = meta.actionable ? 'Review & approve'
                : isExternal      ? 'View live'
                : null;

  const inner = (
    <div className={`rounded-2xl border p-4 transition-colors ${
      meta.actionable
        ? 'border-image-pink bg-image-pink-soft/20 hover:border-image-pink/80'
        : linkable
          ? 'border-image-border bg-white hover:border-image-pink/40'
          : 'border-image-border bg-white'
    }`}>
      <div className="flex items-start justify-between gap-3">
        {/* The photo, when the spotlight has one. Decorative beside the text that already
            names the card, so it carries an empty alt rather than repeating it to a screen
            reader. Absent is a normal state — a spotlight with no photo yet simply shows
            the text, exactly as this card always did. */}
        {/* THE POST'S PICTURE, NOT THE PRO'S FACE (Phil, feedback 026cb350: "Why are the headshots
            different?"). photo_urls[0] is the spotlight's own photo — by the photo rule it is content
            for the post and never the pro's photo of record, so it may legitimately be a client's hair
            or somebody else's shot. Drawn as a small SQUARE with a small radius: the look layer rounds
            every `rounded-xl` to 18px, which turned this 48px square into a circle, and a circle beside a
            name reads as a headshot. The alt says what it is for the same reason. */}
        {photo && (
          <img
            src={photo}
            alt="The photo on this spotlight post"
            className="size-12 rounded-md object-cover shrink-0 border border-image-border"
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1.5">
            <span className={`size-2 rounded-full ${meta.dotClass}`} aria-hidden />
            <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-image-charcoal">
              Pro Spotlight · {meta.label}
            </span>
          </div>
          <div className="font-semibold text-image-black text-sm leading-snug">
            {meta.headline}
          </div>
          <div className="text-[11px] text-image-charcoal mt-1 leading-relaxed">
            {meta.subline}
          </div>
        </div>
        {ctaText && (
          <span className={`text-sm font-semibold shrink-0 whitespace-nowrap ${meta.actionable ? 'text-image-pink' : 'text-image-charcoal'}`}>
            {ctaText}
          </span>
        )}
      </div>
    </div>
  );

  if (isExternal) {
    return (
      <a href={data.posted_url!} target="_blank" rel="noopener noreferrer" className="block group">
        {inner}
      </a>
    );
  }
  if (meta.actionable) {
    return (
      <Link to="/spotlight" className="block group">
        {inner}
      </Link>
    );
  }
  // Passive state — render as a plain div, no link, no arrow.
  return inner;
}

function spotlightMeta(status: string, approvedAt: string | null = null): {
  label: string;
  headline: string;
  subline: string;
  dotClass: string;
  actionable: boolean;
} {
  switch (status) {
    case 'submitted':
    // `draft_failed` is a transient server-side state (Anthropic
    // hiccup or model rotation). The Pro doesn't need to see internal
    // jargon — to them it still reads as "submitted, owner reviewing".
    // The owner sees it as a retry candidate on their side.
    case 'draft_failed':
      return {
        label: 'Submitted',
        headline: 'Your spotlight is in the queue',
        subline: "We're crafting your draft now. We'll send it to you for approval shortly.",
        dotClass: 'bg-blue-500',
        actionable: false,
      };
    case 'drafted':
      return {
        label: 'In review',
        headline: 'Your owner is reviewing the draft',
        subline: "Once they're happy, you'll get to approve it before it goes live.",
        dotClass: 'bg-yellow-500',
        actionable: false,
      };
    case 'pro_review':
      return {
        label: 'Action needed',
        headline: 'Your spotlight is ready for your approval',
        subline: 'Review the draft post and approve it (or request changes) before it gets published.',
        dotClass: 'bg-image-pink animate-pulse',
        actionable: true,
      };
    case 'approved': {
      // No schedule is promised here: the owner posts a spotlight when they run it, and the card
      // says when the pro approved it rather than "soon" (which sat on a card for 26 days).
      const when = approvedAt ? new Date(approvedAt) : null;
      const dated = when && !Number.isNaN(when.getTime())
        ? `Approved ${when.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
        : 'Approved';
      return {
        label: 'Approved',
        headline: `${dated} — waiting for your owner to post it`,
        subline: "Your owner posts it when the spotlight runs. We'll let you know when it is live.",
        dotClass: 'bg-emerald-400',
        actionable: false,
      };
    }
    case 'posted':
      return {
        label: 'Live',
        headline: 'Your spotlight is live!',
        subline: 'Tap to view it on Instagram / Facebook.',
        dotClass: 'bg-emerald-500',
        actionable: false,
      };
    default:
      return {
        label: status,
        headline: 'Your spotlight',
        subline: 'Tap to see status.',
        dotClass: 'bg-gray-400',
        actionable: false,
      };
  }
}

/** HUB-068: a save that landed here but not in the studio's CRM says so, and stays until the next edit. */
function CrmNote({ m, editing }: { m: { isSuccess: boolean; data: unknown }; editing: boolean }) {
  const problem = !editing && m.isSuccess ? crmMirrorProblem(m.data) : null;
  if (!problem) return null;
  return <p role="status" className="text-[11px] text-amber-700 pt-1" data-testid="crm-mirror-problem">{problem}</p>;
}

/**
 * "View current COI" (e33f5778, AWSM-301): the stored address is never opened as it is when it is one of ours — its
 * year-long link dies with Supabase storage, and a link stored on AWS lasts 7 days — so the click signs a five-minute link
 * with the pro's own session and opens that (storagePhoto.ts openStoredFile). Anything else opens as before.
 */
function ViewCoiLink({ url }: { url: string }) {
  const [failed, setFailed] = useState(false);
  const cls = 'block text-[11px] text-image-pink font-medium hover:underline text-center';
  if (!storedFileOf(url)) {
    return <a href={url} target="_blank" rel="noreferrer" className={cls}>View current COI</a>;
  }
  return (
    <>
      <button type="button" className={`${cls} w-full`} data-testid="view-coi"
        onClick={() => { setFailed(false); void openStoredFile(url).then((ok) => { if (!ok) setFailed(true); }); }}>
        View current COI
      </button>
      {failed && <p role="status" className="text-[11px] text-amber-700 text-center">Couldn't open your COI — please try again.</p>}
    </>
  );
}
